import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { IconBell, IconClock, IconCheck } from "@tabler/icons-react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";

type ReminderPayload = {
	id: string;
	reminderId?: string;
	title: string;
	body: string;
};

type ReminderContextValue = {
	triggerReminder: (payload: ReminderPayload) => void;
};

const ReminderContext = createContext<ReminderContextValue | null>(null);

export function useReminderContext(): ReminderContextValue {
	const ctx = useContext(ReminderContext);
	if (!ctx) throw new Error("useReminderContext must be used within ReminderProvider");
	return ctx;
}

function vibrate() {
	if (!navigator.vibrate) return;
	navigator.vibrate([200, 100, 200, 100, 400]);
}

export function ReminderProvider({ children }: { children: ReactNode }) {
	const { getToken } = useAuth();
	const [visible, setVisible] = useState(false);
	const [payload, setPayload] = useState<ReminderPayload | null>(null);

	const triggerReminder = useCallback((p: ReminderPayload) => {
		setPayload(p);
		setVisible(true);
		vibrate();
	}, []);

	useEffect(() => {
		if (!visible) return;
		document.body.style.overflow = "hidden";
		const interval = setInterval(() => {
			if (document.visibilityState === "visible") vibrate();
		}, 3000);
		return () => {
			document.body.style.overflow = "";
			clearInterval(interval);
		};
	}, [visible]);

	const dismiss = useCallback(async () => {
		if (navigator.vibrate) navigator.vibrate(0);

		if (payload?.reminderId) {
			try {
				const token = await getToken();
				if (token) {
					await api.completeReminder(token, payload.reminderId);
				}
			} catch {
				// ignore - reminder still fires
			}
		}

		setVisible(false);
		setPayload(null);
	}, [payload, getToken]);

	return (
		<ReminderContext.Provider value={{ triggerReminder }}>
			{children}
			{visible && payload && (
				<div style={{
					position: "fixed",
					inset: 0,
					zIndex: 9999,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "rgba(0, 0, 0, 0.6)",
					backdropFilter: "blur(4px)",
					WebkitBackdropFilter: "blur(4px)",
					animation: "reminderFadeIn 0.2s ease",
				}}>
					<div style={{
						background: "var(--color-surface)",
						borderRadius: "var(--radius-xl)",
						padding: "2rem 1.5rem 1.5rem",
						maxWidth: 340,
						width: "90%",
						textAlign: "center",
						boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
						animation: "reminderSlideUp 0.3s ease",
						position: "relative",
						overflow: "hidden",
					}}>
						<div style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							height: 4,
							background: "linear-gradient(90deg, var(--color-primary), var(--color-accent, #f59e0b))",
						}} />

						<div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
							<div style={{
								width: 72,
								height: 72,
								borderRadius: "50%",
								background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 70%, #fff))",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								animation: "reminderPulseRing 1.5s ease-out infinite",
							}}>
								<IconBell size={36} color="#fff" strokeWidth={2} />
							</div>
						</div>

						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
							<IconClock size={16} color="var(--color-primary)" />
							<span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
								Pengingat
							</span>
						</div>

						<h2 style={{ margin: "0 0 0.5rem", fontSize: "1.15rem", fontWeight: 700, color: "var(--color-text)", lineHeight: 1.3 }}>
							{payload.title}
						</h2>

						{payload.body && (
							<p style={{ margin: "0 0 1.25rem", fontSize: "0.88rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
								{payload.body}
							</p>
						)}

						<button
							type="button"
							onClick={dismiss}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "0.4rem",
								width: "100%",
								padding: "0.75rem",
								borderRadius: "var(--radius-lg)",
								border: "none",
								background: "var(--color-primary)",
								color: "#fff",
								fontSize: "0.95rem",
								fontWeight: 600,
								cursor: "pointer",
							}}
						>
							<IconCheck size={18} />
							Oke, Mengerti
						</button>

						<p style={{ margin: "0.75rem 0 0", fontSize: "0.7rem", color: "var(--color-text-muted)", opacity: 0.6 }}>
							Pengingat akan ditandai selesai
						</p>
					</div>

					<style>{`
						@keyframes reminderFadeIn { from { opacity: 0; } to { opacity: 1; } }
						@keyframes reminderSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
						@keyframes reminderPulseRing {
							0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 40%, transparent); }
							70% { box-shadow: 0 0 0 15px color-mix(in srgb, var(--color-primary) 0%, transparent); }
							100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 0%, transparent); }
						}
					`}</style>
				</div>
			)}
		</ReminderContext.Provider>
	);
}
